import React, { useMemo } from 'react';
import { BarChartOutlined } from '@ant-design/icons';

// 네이버 쇼핑 순위 알고리즘 역공학 기반 지수 계산
// 네이버는 클릭지수, 판매지수, 리뷰지수, 품질지수, 연관성, 최신성을 종합 반영
function calcIndexes(report, keyword) {
    const p = report?.product;
    const diag = report?.diagnostic;
    const top10 = report?.competitors || [];
    const stats = report?.stats || [];

    if (!p || !diag) return null;

    const myPrice = p.price || 0;
    const _myRank = p.rank || 999;
    const myReviewScore = p.reviewScore || 0;
    const myReviewCount = p.reviewCount || 0;
    const myRecent = p.recentReviewCount || 0;
    const myRecentScore = p.recentReviewScore || myReviewScore;
    const myWish = p.wishCount || 0;
    const myTitle = (p.title || '').toLowerCase();
    const myTags = (p.tags || '').toLowerCase();
    const photoRatioPct = diag.reviewHealth?.photoRatioPct || 0;
    const negRatioPct = diag.reviewHealth?.negRatioPct || 0;
    const avgCompReview = diag.reviewHealth?.avgCompReviewCount || 1;
    const avgCompWish = diag.avgWishCount || 0;
    const avgCompPrice = diag.avgPrice || myPrice;
    const rankedKwCount = diag.kwCompetitiveness?.rankedKwCount || 0;
    const totalKwCount = diag.kwCompetitiveness?.totalKwCount || 1;

    // 경쟁사 평균 평점
    const _avgCompScore = top10.length
        ? top10.reduce((s, c) => s + (c.score || 0), 0) / top10.length
        : myReviewScore;

    // stats에서 6개월 판매량 추출 (인덱스 6 또는 7)
    const parse = v => parseInt((v || '0').toString().replace(/,/g, '')) || 0;
    const my6mSales = parse(stats[6]?.myValue || stats[7]?.myValue || 0);
    const avg6mSales = parse(stats[6]?.value || stats[7]?.value || 0);

    // ── 1. 클릭 점수 ──────────────────────────────────────────
    // 네이버 검색결과에서 클릭률을 결정하는 요소:
    // - 상품명에 검색 키워드 포함 (35%): 볼드 하이라이팅 → CTR 직결
    // - 가격 경쟁력 (30%): 저렴한 상품이 눈에 띔
    // - 리뷰 노출 어필 (25%): 별점+리뷰수가 검색결과에 표시됨
    // - 태그 키워드 (10%): 연관 키워드 추가 노출 기회
    const kwWords = (keyword || '').trim().split(/\s+/).filter(Boolean);
    const titleMatchRatio =
        kwWords.length > 0 ? kwWords.filter(w => myTitle.includes(w)).length / kwWords.length : 0;
    const tagMatchRatio =
        kwWords.length > 0 ? kwWords.filter(w => myTags.includes(w)).length / kwWords.length : 0;
    const priceAdv =
        avgCompPrice > 0
            ? Math.min(Math.max((avgCompPrice - myPrice) / avgCompPrice + 0.5, 0), 1) * 100
            : 50;
    const reviewAppeal = Math.round(
        (myReviewScore / 5) * 60 +
            (Math.min(myReviewCount / Math.max(avgCompReview, 1), 1.5) / 1.5) * 40
    );
    const clickScore = Math.min(
        100,
        Math.round(
            titleMatchRatio * 100 * 0.35 +
                priceAdv * 0.3 +
                reviewAppeal * 0.25 +
                tagMatchRatio * 100 * 0.1
        )
    );
    // 경쟁사 평균 클릭점수 (제목 매칭 제외, 가격+리뷰 기반)
    const avgClickScore = top10.length
        ? Math.round(
              top10.reduce((s, c) => {
                  const pa =
                      Math.min(
                          Math.max(
                              (avgCompPrice - (c.price || avgCompPrice)) / avgCompPrice + 0.5,
                              0
                          ),
                          1
                      ) * 100;
                  const ra = Math.round(
                      ((c.score || 0) / 5) * 60 +
                          (Math.min((c.reviewCount || 0) / avgCompReview, 1.5) / 1.5) * 40
                  );
                  return s + Math.round(pa * 0.45 + ra * 0.4 + 50 * 0.15);
              }, 0) / top10.length
          )
        : 50;

    // ── 2. 판매 점수 ──────────────────────────────────────────
    // 네이버 판매지수: 실제 거래량 (우리는 직접 모름)
    // proxy: 6개월 판매량(stats에 있으면 사용) + 찜수 전환 비율 + 최근 리뷰 활성도
    let salesScore;
    let avgSalesScore;
    if (my6mSales > 0 && avg6mSales > 0) {
        // stats에서 직접 데이터 가져온 경우
        salesScore = Math.min(100, Math.round((Math.min(my6mSales / avg6mSales, 1.5) / 1.5) * 100));
        avgSalesScore = 60; // 평균 기준
    } else {
        // fallback: 찜수 비율 (60%) + 최근 리뷰 활성도 (40%)
        const wishRatio =
            avgCompWish > 0 ? Math.min(myWish / avgCompWish, 1.5) / 1.5 : Math.min(myWish / 100, 1);
        const recentActivity =
            myReviewCount > 0 ? Math.min(myRecent / (myReviewCount * 0.35), 1) : 0;
        salesScore = Math.min(100, Math.round(wishRatio * 60 + recentActivity * 40));
        avgSalesScore = top10.length
            ? Math.round(
                  (top10.reduce((s, c) => {
                      const wr =
                          avgCompWish > 0
                              ? Math.min((c.wishCount || myWish * 0.8) / avgCompWish, 1.5) / 1.5
                              : 0.5;
                      return s + Math.round(wr * 100);
                  }, 0) /
                      top10.length) *
                      0.6 +
                      20
              )
            : 45;
    }

    // ── 3. 리뷰 점수 ──────────────────────────────────────────
    // 네이버 리뷰지수: 리뷰수 + 평점 + 포토리뷰 비율 + 최근성
    // diagnostic.reviewHealth가 이미 이 요소들로 계산됨 (가장 정확)
    const reviewIdxScore = diag.reviewHealth?.score || 0;
    const avgReviewScore = top10.length
        ? Math.round(
              top10.reduce((s, c) => {
                  const rScore = ((c.score || 0) / 5) * 40;
                  const vScore = Math.min((c.reviewCount || 0) / avgCompReview, 1) * 40;
                  return s + Math.round(rScore + vScore + 10); // 포토비율 기본 10점
              }, 0) / top10.length
          )
        : 60;

    // ── 4. 품질 점수 ──────────────────────────────────────────
    // 네이버 품질지수: CS 품질 + 평점 + 부정리뷰 비율 + 포토리뷰(상품 신뢰)
    // 네이버는 환불/교환률도 보지만 우리는 모름 → 부정리뷰 비율로 대체
    const ratingScore = (myReviewScore / 5) * 50;
    const negPenalty = Math.max(0, 30 - negRatioPct * 10); // 부정 1%당 10점 감점
    const photoBonus = Math.min(photoRatioPct * 0.5, 20); // 포토비율 최대 20점
    const qualityScore = Math.min(100, Math.round(ratingScore + negPenalty + photoBonus));
    const avgQuality = top10.length
        ? Math.round(
              top10.reduce((s, c) => s + Math.round(((c.score || 0) / 5) * 80 + 10), 0) /
                  top10.length
          )
        : 60;

    // ── 5. 연관성 ──────────────────────────────────────────────
    // 네이버 연관성지수: 검색어↔상품명/카테고리/태그 매칭도
    // - 상품명 키워드 포함 (45%): 가장 직접적인 연관성 지표
    // - 태그 키워드 포함 (20%): 연관 검색 커버
    // - 카테고리 기반 순위권 키워드 비율 (35%): 실제 노출되는 키워드 수
    const kwRankRatio = totalKwCount > 0 ? rankedKwCount / totalKwCount : 0;
    const relevanceScore = Math.min(
        100,
        Math.round(
            titleMatchRatio * 100 * 0.45 + tagMatchRatio * 100 * 0.2 + kwRankRatio * 100 * 0.35
        )
    );
    const avgRelevance = 50; // 경쟁사 상품명 데이터 없어 중립값

    // ── 6. 최신성 ──────────────────────────────────────────────
    // 네이버 최신성지수: 최근 판매/리뷰 활동 + 등록일(우리는 모름)
    // - 최근 6개월 리뷰 비율이 높을수록 활성 상품
    // - 최근 평점이 전체 평점보다 높으면 상승 트렌드
    const recentRatio = myReviewCount > 0 ? myRecent / myReviewCount : 0;
    const trendBonus =
        myRecentScore > myReviewScore + 0.1 ? 15 : myRecentScore < myReviewScore - 0.1 ? -10 : 0;
    const recencyScore = Math.min(
        100,
        Math.max(0, Math.round(Math.min(recentRatio * 4, 1) * 85 + trendBonus))
    );
    const avgRecency = top10.length
        ? Math.round(
              top10.reduce((s, c) => {
                  const rc = c.reviewCount || 0;
                  // 경쟁사 최근리뷰 없으면 전체의 20% 가정
                  const rr =
                      rc > 0 ? Math.min(((c.recentReviewCount || rc * 0.2) / rc) * 4, 1) : 0.2;
                  return s + Math.round(rr * 85);
              }, 0) / top10.length
          )
        : 25;

    return [
        {
            key: 'click',
            label: '클릭 점수',
            desc: '상품명 키워드 매칭 · 가격 매력도 · 리뷰 노출',
            score: clickScore,
            avgScore: avgClickScore,
            color: '#6366f1',
            badge:
                titleMatchRatio >= 0.8
                    ? { text: '키워드 최적', color: '#10b981' }
                    : titleMatchRatio < 0.3
                      ? { text: '상품명 개선필요', color: '#ef4444' }
                      : null,
        },
        {
            key: 'sales',
            label: '판매 점수',
            desc: my6mSales > 0 ? '6개월 실거래량 기반' : '찜 전환률 · 최근 리뷰 활성도',
            score: salesScore,
            avgScore: avgSalesScore,
            color: '#f97316',
            badge:
                salesScore >= 70
                    ? { text: '판매 활성', color: '#10b981' }
                    : salesScore < 30
                      ? { text: '판매 개선필요', color: '#ef4444' }
                      : null,
        },
        {
            key: 'review',
            label: '리뷰 점수',
            desc: '리뷰수 · 평점 · 포토리뷰 비율 · 불만 비율',
            score: reviewIdxScore,
            avgScore: avgReviewScore,
            color: '#10b981',
            badge:
                reviewIdxScore >= 70
                    ? { text: '리뷰 GOOD', color: '#10b981' }
                    : reviewIdxScore < 40
                      ? { text: '리뷰 부족', color: '#ef4444' }
                      : null,
        },
        {
            key: 'quality',
            label: '품질 점수',
            desc: `평점 ${myReviewScore.toFixed(1)}점 · 불만 ${negRatioPct}% · 포토 ${photoRatioPct}%`,
            score: qualityScore,
            avgScore: avgQuality,
            color: '#3b82f6',
            badge:
                qualityScore >= 80
                    ? { text: '신뢰도 GOOD', color: '#10b981' }
                    : negRatioPct > 5
                      ? { text: 'CS 주의', color: '#ef4444' }
                      : null,
        },
        {
            key: 'relevance',
            label: '연관성',
            desc: '상품명/태그 키워드 매칭 · 순위권 키워드 비율',
            score: relevanceScore,
            avgScore: avgRelevance,
            color: '#8b5cf6',
            badge:
                relevanceScore >= 70
                    ? { text: '키워드 최적화', color: '#10b981' }
                    : relevanceScore < 30
                      ? { text: '상품명 수정필요', color: '#ef4444' }
                      : null,
        },
        {
            key: 'recency',
            label: '최신성',
            desc: '최근 6개월 리뷰 활성도 · 평점 추이',
            score: recencyScore,
            avgScore: avgRecency,
            color: '#14b8a6',
            badge:
                trendBonus > 0
                    ? { text: '상승 트렌드', color: '#10b981' }
                    : trendBonus < 0
                      ? { text: '하락 주의', color: '#ef4444' }
                      : recencyScore < 20
                        ? { text: '활동 저조', color: '#f59e0b' }
                        : null,
        },
    ];
}

const IndexCard = ({ idx, skeleton }) => {
    if (skeleton) {
        return (
            <div
                style={{
                    background: '#fff',
                    borderRadius: 16,
                    border: '1px solid #e2e8f0',
                    padding: '18px 16px 14px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                }}
            >
                <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{idx.label}</div>
                <div style={{ display: 'flex', alignItems: 'center', height: 44 }}>
                    <div style={{ fontSize: 13, color: '#cbd5e1', fontWeight: 600 }}>
                        수집 중...
                    </div>
                </div>
                <div style={{ height: 6, borderRadius: 4, background: '#f1f5f9' }} />
                <div style={{ fontSize: 11, color: '#e2e8f0', lineHeight: 1.5, minHeight: 30 }}>
                    {idx.desc}
                </div>
                <div style={{ height: 22 }} />
            </div>
        );
    }

    const diff = idx.score - idx.avgScore;
    const absDiff = Math.abs(Math.round(diff));
    const isHigher = diff > 2;
    const isLower = diff < -2;
    const diffColor = isHigher ? '#10b981' : isLower ? '#ef4444' : '#94a3b8';
    const diffText = isHigher
        ? `평균보다 ${absDiff}pt 높음`
        : isLower
          ? `평균보다 ${absDiff}pt 낮음`
          : '평균과 동일';

    return (
        <div
            style={{
                background: '#fff',
                borderRadius: 16,
                border: '1px solid #e2e8f0',
                padding: '18px 16px 14px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
            }}
        >
            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, letterSpacing: 0.3 }}>
                {idx.label}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, minHeight: 44 }}>
                <span style={{ fontSize: 40, fontWeight: 900, color: idx.color, lineHeight: 1 }}>
                    {idx.score}
                </span>
                <span
                    style={{
                        fontSize: 11,
                        color: diffColor,
                        fontWeight: 700,
                        paddingBottom: 5,
                        lineHeight: 1.3,
                    }}
                >
                    {diffText}
                </span>
            </div>
            <div style={{ height: 6, borderRadius: 4, background: '#f1f5f9', overflow: 'hidden' }}>
                <div
                    style={{
                        height: '100%',
                        width: `${idx.score}%`,
                        background: idx.color,
                        borderRadius: 4,
                        transition: 'width 0.6s ease',
                    }}
                />
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5, minHeight: 30 }}>
                {idx.desc}
            </div>
            {idx.badge ? (
                <div
                    style={{
                        alignSelf: 'flex-start',
                        fontSize: 11,
                        fontWeight: 700,
                        color: idx.badge.color,
                        background: idx.badge.color + '1a',
                        borderRadius: 20,
                        padding: '2px 10px',
                    }}
                >
                    {idx.badge.text}
                </div>
            ) : (
                <div style={{ height: 22 }} />
            )}
        </div>
    );
};

const IndexAnalysisSection = ({ report, keyword, metricsSettled }) => {
    const indexes = useMemo(() => calcIndexes(report, keyword), [report, keyword]);
    if (!indexes) return null;

    return (
        <>
            <div className="section-title-premium">
                <BarChartOutlined /> 상세 지수 분석
                <span
                    style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 'auto' }}
                >
                    네이버 알고리즘 역공학 기반 · TOP 10 경쟁상품 대비
                </span>
            </div>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: 12,
                    marginBottom: 32,
                }}
            >
                {indexes.map(idx => (
                    <IndexCard
                        key={idx.key}
                        idx={idx}
                        skeleton={!metricsSettled && idx.key === 'relevance'}
                    />
                ))}
            </div>
        </>
    );
};

export default IndexAnalysisSection;
